import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowLeft, Unlock, Lock, Link2, MapPin, Ban, Heart, CalendarClock, Wallet, Landmark, ChevronRight,
  FileText, Droplet, MessageSquare, Power, PowerOff,
} from "lucide-react";
import { getRoomAccess, updateRoomAccess, deactivateRoom, reactivateRoom } from "../../services/roomService";
import { getEventById } from "../../services/eventService";
import Loader from "../../components/common/Loader";
import Card from "../../components/common/Card";
import ConfirmModal from "../../components/common/ConfirmModal";
import WatermarkSettingsModal from "../../components/common/WatermarkSettingsModal";
import WatermarkEventDetailsModal from "../../components/event-details/WatermarkEventDetailsModal";
import SupportBannerModal from "../../components/rooms/SupportBannerModal";
import RoomDescriptionModal from "../../components/rooms/RoomDescriptionModal";
import GreetingMessageModal from "../../components/rooms/GreetingMessageModal";
import { color, radius } from "../../styles/theme";

/**
 * @Room Settings — ported from mobile's setting-a-room screen
 * (src/Screens/setting-a-room). Consolidates access level + pending
 * requests + links out to already-built features (Wallet, Bank Details,
 * Watermark, Locations, Social Links) plus Support Banner and Watermark
 * Event Details, both scoped to @Room events specifically.
 */
const RoomSettings = () => {
  const { t } = useTranslation("rooms");
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const room = state?.room;
  const eventId = room?.EventId || room?.Id || roomId;

  const [loading, setLoading] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState(null);
  const [showWatermarkEventDetails, setShowWatermarkEventDetails] = useState(false);
  const [showSupportBanner, setShowSupportBanner] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [togglingRoomActive, setTogglingRoomActive] = useState(false);

  const load = () => {
    setLoading(true);
    getRoomAccess(eventId)
      .then((res) => setIsPrivate(res.data?.AccessMode === 2))
      .catch(() => toast.error(t("roomSettings.toasts.loadAccessFailed")))
      .finally(() => setLoading(false));
    getEventById(eventId)
      .then((res) => setEvent(res.data?.Result || res.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Same Event.IsClosed-backed deactivate/reactivate already built for
  // EventDetails.jsx's "⋮" menu (roomService.js) — surfaced here too, since
  // Settings is the more natural home for it per the QA ticket. Reactivating
  // is a plain reversal so it fires immediately; deactivating hides the room
  // from participants, so it's confirmed first, same as there.
  const handleToggleRoomActive = async () => {
    if (event?.IsClosed) {
      setTogglingRoomActive(true);
      try {
        const res = await reactivateRoom(eventId);
        toast.success(res.data?.Message || t("roomSettings.toasts.roomReactivated"));
        load();
      } catch (err) {
        toast.error(err.response?.data?.Message || t("roomSettings.toasts.roomReactivateFailed"));
      } finally {
        setTogglingRoomActive(false);
      }
      return;
    }
    setShowDeactivateConfirm(true);
  };

  const confirmDeactivateRoom = async () => {
    setTogglingRoomActive(true);
    try {
      const res = await deactivateRoom(eventId);
      toast.success(res.data?.Message || t("roomSettings.toasts.roomDeactivated"));
      setShowDeactivateConfirm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomSettings.toasts.roomDeactivateFailed"));
    } finally {
      setTogglingRoomActive(false);
    }
  };

  const handleToggleAccess = async (makePrivate) => {
    if (makePrivate === isPrivate) return;
    setSaving(true);
    try {
      const res = await updateRoomAccess(eventId, makePrivate ? 2 : 1);
      toast.success(res.data?.Message || t("roomSettings.toasts.accessUpdated"));
      setIsPrivate(makePrivate);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomSettings.toasts.accessUpdateFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate(`/rooms/${roomId}/galleries`, { state: { room } })}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("roomSettings.backButton")}
      </button>
      <h2 style={s.title}>{t("roomSettings.title")}</h2>

      <Card style={s.section}>
        <h4 style={s.sectionTitle}>{t("roomSettings.accessLevelSection")}</h4>
        <div style={s.accessRow}>
          <button
            type="button"
            style={{ ...s.accessOption, ...(!isPrivate ? s.accessOptionActive : {}) }}
            onClick={() => handleToggleAccess(false)}
            disabled={saving}
          >
            <Unlock size={14} strokeWidth={2.25} /> {t("roomSettings.publicOption")}
          </button>
          <button
            type="button"
            style={{ ...s.accessOption, ...(isPrivate ? s.accessOptionActive : {}) }}
            onClick={() => handleToggleAccess(true)}
            disabled={saving}
          >
            <Lock size={14} strokeWidth={2.25} /> {t("roomSettings.privateOption")}
          </button>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" style={s.subLink} onClick={() => navigate(`/rooms/${roomId}/pending-approval`, { state: { room} })}>
          {t("roomSettings.viewPendingRequests")} <ChevronRight size={13} strokeWidth={2.5} />
        </button>
      </Card>

      <Card style={s.section}>
        <h4 style={s.sectionTitle}>{t("roomSettings.managementSection")}</h4>
        <SettingsRow icon={FileText} title={t("roomSettings.descriptionRow.title")} description={event?.Description || t("roomSettings.descriptionRow.description")} onClick={() => setShowDescription(true)} />
        <SettingsRow icon={Link2} title={t("roomSettings.socialLinksRow.title")} description={t("roomSettings.socialLinksRow.description")} onClick={() => navigate("/rooms/social-links")} />
        <SettingsRow icon={MapPin} title={t("roomSettings.locationsRow.title")} description={t("roomSettings.locationsRow.description")} onClick={() => navigate("/locations")} />
        <SettingsRow icon={Ban} title={t("roomSettings.blockedUsersRow.title")} description={t("roomSettings.blockedUsersRow.description")} onClick={() => navigate(`/events/${eventId}`, { state: { openTab: "Participants" } })} last />
      </Card>

      <Card style={s.section}>
        <h4 style={s.sectionTitle}>{t("roomSettings.watermarkSection")}</h4>
        <SettingsRow icon={Droplet} title={t("roomSettings.uploadWatermarkRow.title")} description={t("roomSettings.uploadWatermarkRow.description")} onClick={() => setShowWatermark(true)} />
        <SettingsRow icon={CalendarClock} title={t("roomSettings.watermarkEventDetailsRow.title")} description={t("roomSettings.watermarkEventDetailsRow.description")} onClick={() => setShowWatermarkEventDetails(true)} />
        <SettingsRow icon={Heart} title={t("roomSettings.supportBannerRow.title")} description={t("roomSettings.supportBannerRow.description")} onClick={() => setShowSupportBanner(true)} last />
      </Card>

      <Card style={s.section}>
        <h4 style={s.sectionTitle}>{t("roomSettings.communicationSection")}</h4>
        <SettingsRow icon={MessageSquare} title={t("roomSettings.greetingRow.title")} description={t("roomSettings.greetingRow.description")} onClick={() => setShowGreeting(true)} last />
      </Card>

      <Card style={s.section}>
        <h4 style={s.sectionTitle}>{t("roomSettings.paymentsSection")}</h4>
        <SettingsRow icon={Wallet} title={t("roomSettings.walletRow.title")} description={t("roomSettings.walletRow.description")} onClick={() => navigate(`/wallet`)} />
        <SettingsRow icon={Landmark} title={t("roomSettings.bankDetailsRow.title")} description={t("roomSettings.bankDetailsRow.description")} onClick={() => navigate("/wallet/bank-details")} last />
      </Card>

      <Card style={{ ...s.section, ...s.dangerSection }}>
        <button
          type="button"
          className="row-link"
          style={s.dangerRow}
          onClick={handleToggleRoomActive}
          disabled={togglingRoomActive}
        >
          <span style={s.dangerIconBadge}>
            {event?.IsClosed ? <Power size={16} strokeWidth={2} /> : <PowerOff size={16} strokeWidth={2} />}
          </span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <div style={s.dangerRowTitle}>{event?.IsClosed ? t("roomSettings.reactivateRoomRow.title") : t("roomSettings.deactivateRoomRow.title")}</div>
            <div style={s.rowDesc}>{event?.IsClosed ? t("roomSettings.reactivateRoomRow.description") : t("roomSettings.deactivateRoomRow.description")}</div>
          </span>
        </button>
      </Card>

      {showDescription && (
        <RoomDescriptionModal
          eventId={eventId}
          initialDescription={event?.Description}
          onClose={() => setShowDescription(false)}
          onUpdated={(newDescription) => { setShowDescription(false); setEvent((prev) => (prev ? { ...prev, Description: newDescription } : prev)); }}
        />
      )}

      {showWatermark && (
        <WatermarkSettingsModal onClose={() => setShowWatermark(false)} onSaved={() => {}} />
      )}

      {showGreeting && (
        <GreetingMessageModal onClose={() => setShowGreeting(false)} />
      )}

      {showSupportBanner && (
        <SupportBannerModal eventId={eventId} onClose={() => setShowSupportBanner(false)} />
      )}

      {showWatermarkEventDetails && (
        <WatermarkEventDetailsModal
          event={event}
          eventId={eventId}
          onClose={() => setShowWatermarkEventDetails(false)}
          onUpdated={() => { setShowWatermarkEventDetails(false); load(); }}
        />
      )}

      {showDeactivateConfirm && (
        <ConfirmModal
          title={t("roomSettings.deactivateConfirm.title")}
          message={t("roomSettings.deactivateConfirm.message")}
          confirmText={t("roomSettings.deactivateConfirm.confirmText")}
          danger
          loading={togglingRoomActive}
          onConfirm={confirmDeactivateRoom}
          onCancel={() => !togglingRoomActive && setShowDeactivateConfirm(false)}
        />
      )}
    </div>
  );
};

const SettingsRow = ({ icon: Icon, title, description, onClick, last = false }) => (
  <button type="button" className="row-link" style={{ ...s.row, ...(last ? { borderBottom: "none" } : {}) }} onClick={onClick}>
    <span style={s.rowIconBadge}><Icon size={16} strokeWidth={2} /></span>
    <span style={{ flex: 1, textAlign: "left" }}>
      <div style={s.rowTitle}>{title}</div>
      {description && <div style={s.rowDesc}>{description}</div>}
    </span>
    <ChevronRight size={16} color={color.gray400} />
  </button>
);

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  section: { padding: "18px 20px", marginBottom: 18, maxWidth: 480 },
  sectionTitle: { margin: "0 0 14px", fontSize: 12, fontWeight: 700, color: color.textMuted, textTransform: "uppercase", letterSpacing: 0.6 },
  accessRow: { display: "flex", gap: 10 },
  accessOption: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
    flex: 1, padding: "10px 12px", borderRadius: radius.sm, border: `1.5px solid ${color.border}`,
    background: color.gray50, color: color.gray700, fontWeight: 600, fontSize: 13, cursor: "pointer",
  },
  accessOptionActive: { border: `1.5px solid ${color.primary500}`, background: color.primary50, color: color.primary600 },
  subLink: { color: color.primary600, fontWeight: 600, marginTop: 12, padding: "4px 6px" },
  row: {
    display: "flex", alignItems: "center", gap: 13, width: "100%", padding: "13px 0",
    borderBottom: `1px solid ${color.gray100}`,
  },
  rowIconBadge: {
    width: 32, height: 32, borderRadius: radius.sm, background: color.gray100, color: color.gray600,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  rowTitle: { fontSize: 13.5, fontWeight: 600, color: color.textPrimary },
  rowDesc: { fontSize: 11.5, color: color.textMuted, marginTop: 2 },
  dangerSection: { borderColor: color.danger50 },
  dangerRow: { display: "flex", alignItems: "center", gap: 13, width: "100%" },
  dangerIconBadge: {
    width: 32, height: 32, borderRadius: radius.sm, background: color.danger50, color: color.danger500,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  dangerRowTitle: { fontSize: 13.5, fontWeight: 600, color: color.danger500 },
};

export default RoomSettings;
